/**
 * CampaignAnalytics
 *
 * Displays key survey and video metrics for a testimonial campaign.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/layout/card';
import { MetricCard } from './metric-card';
import { 
  Clock, 
  MousePointer, 
  Video, 
  BarChart, 
  TimerReset
} from 'lucide-react';

// Helper function to format time in ms to readable format
const formatTime = (timeInMs) => {
  if (!timeInMs && timeInMs !== 0) return 'N/A';
  
  const seconds = Math.floor(timeInMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes === 0) {
    return `${seconds} sec`;
  }
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} min`;
};

export default function CampaignAnalytics({ campaign }) {
  // Early return if no campaign data
  if (!campaign) return null;
  
  // Extract relevant metrics
  const {
    surveyClicks = 0,
    surveyTotalCompletionTime = 0,
    surveyResponseCountForTime = 0,
    totalVideoDuration = 0,
    videoResponseCount = 0,
    averageCompletionTime = 0,
    averageVideoDuration = 0,
    responsesCount = 0,
    createdAt
  } = campaign;
  
  // Calculate campaign age based on createdAt timestamp
  const calculateCampaignAge = () => {
    if (!createdAt) return 'N/A';
    
    // Extract seconds from Firestore timestamp
    const seconds = createdAt._seconds || createdAt.seconds;
    if (!seconds) return 'N/A';
    
    // Convert to milliseconds for Date constructor
    const createdDate = new Date(seconds * 1000);
    const now = new Date();
    
    // Calculate the difference in milliseconds
    const diffMs = now - createdDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Format the age in a human-readable way
    let ageString = '';
    
    if (diffDays < 1) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        ageString = `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
      } else {
        ageString = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
      }
    } else if (diffDays < 30) {
      ageString = `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      ageString = `${months} ${months === 1 ? 'month' : 'months'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      ageString = `${years} ${years === 1 ? 'year' : 'years'}`;
    }
    
    return ageString;
  };
  
  return (
    <div className="mt-8 space-y-8">
      {/* Survey Performance Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium text-gray-900 dark:text-white">Survey Performance</CardTitle>
          <CardDescription>Key metrics about engagement and responses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            <MetricCard
              title="Survey Clicks"
              value={surveyClicks}
              icon={MousePointer}
              description="Users who started your survey"
            />
            
            <MetricCard
              title="Response Rate"
              value={surveyClicks > 0 ? `${Math.round((responsesCount / surveyClicks) * 100)}%` : '0%'}
              icon={BarChart}
              description={`${responsesCount} of ${surveyClicks} users completed`}
            />
            
            <MetricCard
              title="Avg. Completion Time"
              value={formatTime(averageCompletionTime)}
              icon={Clock}
              description={`Based on ${surveyResponseCountForTime} submissions`}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Video Content Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium text-gray-900 dark:text-white">Campaign Insights</CardTitle>
          <CardDescription>Video performance data and campaign timeline metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            <MetricCard
              title="Avg. Video Length"
              value={formatTime(averageVideoDuration)}
              icon={Video}
              description="Average duration of videos"
            />
            
            <MetricCard
              title="Total Video Duration"
              value={formatTime(totalVideoDuration)}
              icon={TimerReset}
              description="Total video content collected"
            />
            
            <MetricCard
              title="Campaign Age"
              value={calculateCampaignAge()}
              icon={Clock}
              description="Age since campaign creation"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}